import apiClient from '../config/axiosConfig.js';
import { uploadBytes } from './resumableUpload.js';

export function requestThumbnailRegeneration(fileid, options = {}) {
    // `options` is passed through to axios so bulk callers can opt out of the
    // global error toast ({ skipGlobalErrorToast: true }) and show one summary.
    return apiClient.post(
        `/api/files/thumbnail/regenerate?id=${encodeURIComponent(fileid)}`,
        { },
        options
    );
}


export async function uploadFile(file, transcribe, metadata, tags, onProgress, options = {}) {
    try {
        // 1. Initialize upload (Get Signed URL). `options` is forwarded to the
        // init/finalize apiClient calls so a bulk caller can opt out of the
        // global error toast ({ skipGlobalErrorToast: true }) and show one
        // summary instead of one toast per failed file.
        const initResponse = await apiClient.post('/api/files/upload/init', {
            originalname: file.name,
            mimetype: file.type,
            size: file.size
        }, options);

        const { fileId, signedUrl, gcsDestinationPath, uploadMode } = initResponse.data;

        // 2. Upload directly to GCS (no interceptor / auth token). `uploadBytes`
        // picks the simple single-PUT or a resumable session per the backend's
        // `uploadMode`. `onProgress(loaded, total)` reports RAW byte counts so
        // callers can aggregate throughput for speed/ETA; `options.signal`
        // (AbortSignal) lets a caller cancel an in-flight upload.
        await uploadBytes({ signedUrl, uploadMode }, file, {
            signal: options.signal,
            onProgress: (loaded, total) => {
                if (onProgress) onProgress(loaded, total || file.size);
            },
        });

        // 3. Finalize upload
        const finalizeResponse = await apiClient.post('/api/files/upload/finalize', {
            fileId,
            gcsDestinationPath,
            originalname: file.name,
            mimetype: file.type,
            size: file.size,
            encoding: '7bit',
            transcribe: transcribe,
            metadata: metadata || {},
            tags: tags || []
        }, options);

        return finalizeResponse.data;
    } catch (error) {
        console.error('Upload Error:', error);
        throw error;
    }
}

export async function getFiles() {
    try {
        const response = await apiClient.get('/api/files/get');
        return response.data;
    } catch (error) {
        console.error("Error fetching files:", error);
        throw error;
    }
}

/** Fetch a single file document by ID. GET /api/files/get-one?id=<fileId> */
export async function getFile(fileId, options = {}) {
    const response = await apiClient.get('/api/files/get-one', { params: { id: fileId }, ...options });
    return response.data;
}

//
export async function generateFileHash(fileId, options = {}) {
    try {
        // `options` is passed through to axios so bulk callers can opt out of the
        // global error toast ({ skipGlobalErrorToast: true }) and show one summary.
        const response = await apiClient.get(`/api/files/generate-hash?id=` + encodeURIComponent(fileId), options);
        return response.data.hash;
    } catch (error) {
        console.error("Error generating hash:", error);
        throw error;
    }
}



//

// Update to accept hash
export async function checkFileExists(name, size, hash, options = {}) {
    // M-B5: let failures propagate — silently returning false reported
    // "no duplicate" on every backend error.
    // `options` is forwarded to axios so bulk callers (e.g. the import wizard's
    // duplicate check over 1000+ files) can pass { skipGlobalErrorToast: true }
    // and show ONE summary instead of one toast per failed lookup.
    const response = await apiClient.get(`/api/files/exists`, {
        params: { name, size, hash },
        ...options,
    });
    return response.data.exists;
}

export function deleteFile(fileid, options = {}) {
    // BUG-6: return the axios promise directly so failures REJECT (the old
    // `new Promise(resolve => ...)` with no catch/reject left failed deletes
    // pending forever and reported false success to callers).
    // `options` is passed through to axios — callers doing their own bulk-error
    // summary pass { skipGlobalErrorToast: true } to suppress per-file toasts.
    return apiClient.delete('/api/files/delete?id=' + encodeURIComponent(fileid), options);
}

/**
 * Fetches a file's raw bytes as a Blob (via the backend, so auth headers are
 * attached). Used both by single-file download and the bulk download/zip path.
 * `options` is forwarded to axios so bulk callers can pass
 * { skipGlobalErrorToast: true } and show one summary instead of one toast per
 * failed file.
 */
export async function fetchFileBlob(id, options = {}) {
    const response = await apiClient.get('/api/files/download', {
        params: { id },
        responseType: 'blob', // CRITICAL: prevents axios from trying to parse JSON
        ...options,
    });
    return response.data;
}

/** Triggers a browser "Save As" for an in-memory Blob. */
export function saveBlob(blob, name) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export function downloadFile(id, name) {
    // We use apiClient to automatically attach the Auth Headers
    fetchFileBlob(id)
        .then(blob => saveBlob(blob, name))
        .catch(error => {
            console.error("Download failed:", error);
        });
}


// ─── Version API ──────────────────────────────────────────────────────────────

/**
 * Uploads a new version of an existing file.
 */
export async function uploadNewVersion(fileId, file, transcribe = false, onProgress) {
    try {
        // 1. Initialize version upload
        const initResponse = await apiClient.post('/api/files/upload-version/init', {
            fileId,
            originalname: file.name,
            mimetype: file.type,
            size: file.size
        });

        const { versionId, signedUrl, gcsDestinationPath, nextVersionNumber, uploadMode } = initResponse.data;

        // 2. Upload directly to GCS (simple PUT or resumable, per `uploadMode`).
        // This path reports PERCENT progress, so convert raw bytes here.
        await uploadBytes({ signedUrl, uploadMode }, file, {
            onProgress: (loaded, total) => {
                if (onProgress && total) onProgress(Math.round((loaded * 100) / total));
            },
        });

        // 3. Finalize version upload
        const finalizeResponse = await apiClient.post('/api/files/upload-version/finalize', {
            fileId,
            versionId,
            gcsDestinationPath,
            originalname: file.name,
            mimetype: file.type,
            size: file.size,
            encoding: '7bit',
            transcribe,
            nextVersionNumber
        });

        return finalizeResponse.data;
    } catch (err) {
        console.error('Upload version error:', err);
        throw err;
    }
}

/**
 * Returns the full version history (all past versions, not the current one).
 * GET /api/files/versions?id=<fileId>
 */
export async function getFileVersions(fileId) {
    const res = await apiClient.get(`/api/files/versions?id=${fileId}`);
    return res.data; // FileVersion[]
}

/**
 * Restores a past version to become the current version.
 * POST /api/files/versions/restore?id=<fileId>&versionId=<versionId>
 */
export async function restoreFileVersion(fileId, versionId) {
    const res = await apiClient.post(
        `/api/files/versions/restore?id=${fileId}&versionId=${versionId}`
    );
    return res.data;
}

/**
 * SEC-7: returns a short-lived, signed GCS URL for downloading/sharing a file
 * (or a specific version). Unlike buildAuthUrl, this does NOT embed the user's
 * Firebase token, so the resulting URL is safe to put in an <a>/<audio> tag or
 * copy as a "Share Link".
 */
export async function getSignedDownloadUrl(fileId, versionId) {
    const params = { id: fileId };
    if (versionId) params.versionId = versionId;
    const res = await apiClient.get('/api/files/signed-url', { params });
    return res.data.url;
}

/**
 * Builds a download URL for a specific old version.
 * SEC-7: now a signed GCS URL instead of a token-bearing backend URL.
 */
export async function buildVersionDownloadUrl(fileId, versionId) {
    return getSignedDownloadUrl(fileId, versionId);
}


/**
 * Updates specific metadata fields for a file.
 * POST /api/files/metadata/update?id=<fileId>
 * * @param {string} fileId - The ID of the file to update.
 * @param {Object} metadataUpdates - The key-value pairs of metadata to change.
 */
export async function updateFileMetadata(fileId, metadataUpdates) {
    try {
        const response = await apiClient.post(`/api/files/metadata/update?id=${fileId}`, {
            metadata: metadataUpdates
        });
        return response.data;
    } catch (error) {
        console.error("Error updating file metadata:", error);
        throw error; // Re-throw so the UI can handle the error state
    }
}
// ── Soft locks (§10) ────────────────────────────────────────────────────────

/**
 * Who is working on this file, if anyone.
 *
 * Read-level on purpose: a lock that only its holder can see cannot do the one
 * job it has, which is telling the SECOND person before they start.
 */
export async function getFileLock(fileId, options = {}) {
    const res = await apiClient.get('/api/files/lock', { params: { id: fileId }, ...options });
    return res.data?.lock ?? null;
}

/** Take or renew a lock. Rejects with a 409 carrying `holder` when taken. */
export async function acquireFileLock(fileId, { note } = {}, options = {}) {
    const res = await apiClient.post('/api/files/lock', { fileId, note }, options);
    return res.data?.lock ?? null;
}

/** Release a lock. The holder always may; an admin may break someone else's. */
export async function releaseFileLock(fileId, options = {}) {
    const res = await apiClient.delete('/api/files/lock', { params: { id: fileId }, ...options });
    return res.data?.released === true;
}
